import { buildUrl, createRequest } from './common/httpUtils'
import { appendAuthToken } from './common/session'

export type RfidTagAttributes = {
  name: string
  identifier: string
  payload?: string | null
}

export type CreateRfidTagEventPayload = {
  rfid_tag_id: number
  event_type_id: number
  occurred_at: string
  notes?: string
  latitude?: number
  longitude?: number
  device_id?: DeviceId
}

const responseErrorMessage = (data: any, fallback: string): string => {
  if (typeof data?.error === 'string' && data.error.length > 0) return data.error
  if (typeof data?.error_message === 'string' && data.error_message.length > 0) {
    return data.error_message
  }
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join('、')
  return fallback
}

const readJson = async (response: Response): Promise<any> => {
  try {
    return await response.json()
  } catch (error) {
    return {}
  }
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await readJson(response)
  if (!response.ok) {
    throw new Error(responseErrorMessage(data, response.statusText || 'RFID APIの処理に失敗しました。'))
  }
  return data as T
}

const collectionPath = (groupId: GroupId): string => (
  `/api/v1/groups/${groupId}/rfid_tags`
)

const memberPath = (groupId: GroupId, rfidTagId: number): string => (
  `${collectionPath(groupId)}/${rfidTagId}`
)

export const getGroupRfidTags = async (groupId: GroupId): Promise<IRfidTag[]> => {
  const url = buildUrl(appendAuthToken(collectionPath(groupId)))
  const response = await fetch(createRequest(url, 'GET'))
  const data = await handleResponse<{ rfid_tags?: IRfidTag[] }>(response)
  return data.rfid_tags ?? []
}

export const createRfidTag = async (
  groupId: GroupId,
  attributes: RfidTagAttributes,
): Promise<IRfidTag> => {
  const url = buildUrl(appendAuthToken(collectionPath(groupId)))
  const response = await fetch(createRequest(
    url,
    'POST',
    true,
    'application/json',
    { rfid_tag: attributes },
  ))
  const data = await handleResponse<{ rfid_tag: IRfidTag }>(response)
  return data.rfid_tag
}

export const updateRfidTag = async (
  groupId: GroupId,
  rfidTagId: number,
  attributes: RfidTagAttributes,
): Promise<IRfidTag> => {
  const url = buildUrl(appendAuthToken(memberPath(groupId, rfidTagId)))
  const response = await fetch(createRequest(
    url,
    'PATCH',
    true,
    'application/json',
    { rfid_tag: attributes },
  ))
  const data = await handleResponse<{ rfid_tag: IRfidTag }>(response)
  return data.rfid_tag
}

export const deleteRfidTag = async (
  groupId: GroupId,
  rfidTagId: number,
): Promise<void> => {
  const url = buildUrl(appendAuthToken(memberPath(groupId, rfidTagId)))
  const response = await fetch(createRequest(url, 'DELETE'))
  await handleResponse<Record<string, unknown>>(response)
}

export const createRfidTagEvent = async (
  groupId: GroupId,
  payload: CreateRfidTagEventPayload,
): Promise<IRfidTagEvent> => {
  const path = `/api/v1/groups/${groupId}/rfid_tag_events`
  const url = buildUrl(appendAuthToken(path))
  const response = await fetch(createRequest(
    url,
    'POST',
    true,
    'application/json',
    { rfid_tag_event: payload },
  ))
  const data = await handleResponse<{ rfid_tag_event: IRfidTagEvent }>(response)
  return data.rfid_tag_event
}
